<template>
  <div class="container mx-auto px-4 py-8">
    <!-- Hero Section -->
    <section class="text-center py-16 sm:py-24">
      <h1 class="text-4xl sm:text-6xl font-extrabold tracking-tight mb-4">
        <span class="block text-slate-100">Every Tool You Need,</span>
        <span class="block bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
          One Click Away.
        </span>
      </h1>
      <p class="max-w-2xl mx-auto text-lg text-slate-400 mb-8">
        A complete suite of developer utilities designed to boost your productivity.
        Open source, free, and privacy-focused.
      </p>

      <!-- Search Input -->
      <div class="max-w-md mx-auto relative group">
        <div class="absolute -inset-0.5 bg-gradient-to-r from-primary-500 to-primary-700 rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
        <div class="relative">
          <input 
            v-model="searchQuery"
            type="text" 
            placeholder="Search tools (e.g., 'JSON', 'UUID')..." 
            class="block w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent transition-all"
          />
          <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500">
             <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
        </div>
      </div>
    </section>

    <!-- Tools Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      <router-link v-for="tool in filteredTools" :to="tool.route" :key="tool.name" class="block h-full">
        <ToolCard :tool="tool"></ToolCard>
      </router-link>
    </div>
    
    <!-- Empty State -->
    <div v-if="filteredTools.length === 0" class="text-center py-12">
      <p class="text-slate-500 text-lg">No tools found matching "{{ searchQuery }}"</p>
      <button @click="searchQuery = ''" class="mt-4 text-primary-400 hover:text-primary-300 font-medium">Clear search</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ToolCard from '@/components/ToolCard.vue';
import { Tool } from '@/types/types';

export default defineComponent({
  name: 'HomeView',
  components: {
    ToolCard
  },
  data() {
    return {
      searchQuery: '',
      tools: [
        { name: 'QR Code Generator', route: '/tools/qrcode', icon: 'img/qrcode_logo.png', description: 'Generate custom QR Codes instantly.' },
        { name: 'Timestamp Generator', route: '/tools/timestamp', icon: 'img/timestamp_logo.png', description: 'Convert dates to timestamps and vice versa.' },
        { name: 'Base64 Converter', route: '/tools/base64', icon: 'img/base64_logo.png', description: 'Encode and decode Base64 strings.' },
        { name: 'UUID Generator', route: '/tools/uuid', icon: 'img/uuid_logo.png', description: 'Generate secure UUIDs (v4).' },
        { name: 'JSON Formatter', route: '/tools/json', icon: 'img/json_logo.png', description: 'Prettify and validate JSON data.' },
        { name: 'XML Formatter', route: '/tools/xml', icon: 'img/xml_logo.png', description: 'Format and minify XML documents.' },
        { name: 'Image Resizer', route: '/tools/image-resizer', icon: 'img/image_resizer_logo.png', description: 'Resize and optimize images easily.' },
        { name: 'Markdown Preview', route: '/tools/markdown', icon: 'img/markdown_logo.png', description: 'Preview Markdown content in real-time.' },
        { name: 'Web Storage', route: '/tools/web-storage', icon: 'img/web_storage_logo.png', description: 'Inspect LocalStorage and SessionStorage.' },
        { name: 'Password Generator', route: '/tools/password', icon: 'img/password_logo.png', description: 'Create strong, secure passwords.' },
        { name: 'Browser Details', route: '/tools/browser-details', icon: 'img/browser_logo.png', description: 'Analyze your browser information.' },
        { name: 'Color Picker', route: '/tools/color-picker', icon: 'img/color_picker_logo.png', description: 'Convert between HEX, RGB, and HSL.' },
        { name: 'Text Utility', route: '/tools/text-utility', icon: 'img/text_utility_logo.png', description: 'Count words, chars, and manipulate text.' },
        { name: 'JWT Decoder', route: '/tools/jwt-decoder', icon: 'img/jwt-decoder_logo.png', description: 'Decode and inspect JWT tokens.' },
        { name: 'Hashing Tools', route: '/tools/hashing-tools', icon: 'img/hashing_tools_logo.png', description: 'Generate MD5, SHA-1, SHA-256 hashes.' },
        { name: 'Git Cheatsheet', route: '/tools/git-cheatsheet', icon: 'img/git_cheatsheet_logo.png',description: 'Essential Git commands reference.'},
        { name: 'Prompt Builder', route: '/tools/prompt-template-builder', icon: 'img/promptBuilderLogo.webp', description: 'Build and manage AI prompts.'},
        { name: 'VS Code Hotkeys', route: '/tools/vscode-hotkeys', icon: 'img/vscode_hotkeys_logo.webp', description: 'Master VS Code keyboard shortcuts.'},
        { name: 'Mermaid Converter', route: '/tools/mermaid-converter', icon: 'img/mermaid_logo.svg', description: 'Visualize Mermaid diagrams.'},
        { name: 'PDF Page Remover', route: '/tools/pdf-page-remover', icon: 'img/pdf_logo.svg', description: 'Extract or remove pages from PDFs.'},
      ] as Tool[],
    };
  },
  computed: {
    filteredTools(): Tool[] {
        if (!this.searchQuery) return this.tools;
        const query = this.searchQuery.toLowerCase();
        return this.tools.filter(tool => 
            tool.name.toLowerCase().includes(query) || 
            tool.description.toLowerCase().includes(query)
        );
    }
  }
});
</script>

<style scoped>
/* Scoped styles if necessary, but Tailwind handles most */
</style>
